import React, { useState } from "react";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Database, Download, FileText, FileSpreadsheet, Archive, CheckCircle, AlertCircle, Clock, Users, BookOpen, Calendar, CreditCard, History as HistoryIcon } from "lucide-react";
import { useFirebase } from "@/contexts/FirebaseContext";
import { useToast } from "@/hooks/use-toast";
import { collection, getDocs } from "firebase/firestore";
import JSZip from "jszip";
import * as XLSX from "xlsx";

const SystemBackup = () => {
  const { getFirestore } = useFirebase();
  const { toast } = useToast();
  const [isGeneratingBackup, setIsGeneratingBackup] = useState(false);
  const [backupProgress, setBackupProgress] = useState<string>("");
  const [lastBackupDate, setLastBackupDate] = useState<string | null>(null);

  // Comprehensive Backup Function
  const generateSystemBackup = async () => {
    setIsGeneratingBackup(true);
    setBackupProgress("Initializing backup process...");
    
    try {
      const db = getFirestore();
      const zip = new JSZip();
      
      // Create folders for different data types
      const pdfFolder = zip.folder("PDF_Reports");
      const excelFolder = zip.folder("Excel_Data");
      
      setBackupProgress("Fetching data from database...");
      
      // Fetch all data from Firestore
      const collections = [
        "users",
        "courses", 
        "enrollments",
        "schedules",
        "lessons",
        "payments",
        "instructor_availability"
      ];
      
      const allData: { [key: string]: any[] } = {};
      
      // Fetch data from all collections
      for (const collectionName of collections) {
        try {
          setBackupProgress(`Fetching ${collectionName}...`);
          const snapshot = await getDocs(collection(db, collectionName));
          allData[collectionName] = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
        } catch (error) {
          console.error(`Error fetching ${collectionName}:`, error);
          allData[collectionName] = [];
        }
      }
      
      setBackupProgress("Generating Excel files...");
      
      // Generate Excel files for each collection
      for (const [collectionName, data] of Object.entries(allData)) {
        if (data.length > 0) {
          const worksheet = XLSX.utils.json_to_sheet(data);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, collectionName);
          
          const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
          excelFolder?.file(`${collectionName}.xlsx`, excelBuffer);
        }
      }
      
      setBackupProgress("Creating comprehensive reports...");
      
      // Generate comprehensive Excel report
      const comprehensiveData = {
        summary: {
          totalStudents: allData.users.filter(u => u.role === 'student').length,
          totalInstructors: allData.users.filter(u => u.role === 'instructor').length,
          totalCourses: allData.courses.length,
          totalEnrollments: allData.enrollments.length,
          totalSchedules: allData.schedules.length,
          totalLessons: allData.lessons.length,
          totalPayments: allData.payments.length,
          backupDate: new Date().toISOString()
        },
        students: allData.users.filter(u => u.role === 'student').map(student => ({
          id: student.id,
          name: `${student.firstName || ''} ${student.lastName || ''}`.trim(),
          email: student.email,
          phone: student.phone,
          address: student.address,
          postcode: student.postcode,
          gearbox: student.gearbox,
          licenseNumber: student.licenseNumber,
          createdAt: student.createdAt,
          enrollments: allData.enrollments.filter(e => e.studentId === student.id).length,
          schedules: allData.schedules.filter(s => s.studentId === student.id).length,
          completedLessons: allData.lessons.filter(l => l.studentId === student.id && l.status === 'completed').length
        })),
        instructors: allData.users.filter(u => u.role === 'instructor').map(instructor => ({
          id: instructor.id,
          name: `${instructor.firstName || ''} ${instructor.lastName || ''}`.trim(),
          email: instructor.email,
          phone: instructor.phone,
          specializations: instructor.specializations,
          createdAt: instructor.createdAt,
          students: allData.enrollments.filter(e => e.instructorId === instructor.id).length,
          schedules: allData.schedules.filter(s => s.instructorId === instructor.id).length
        })),
        courses: allData.courses.map(course => ({
          id: course.id,
          title: course.title,
          type: course.type,
          subtype: course.subtype,
          totalHours: course.totalHours,
          price: course.price,
          vehicleType: course.vehicleType,
          licenseType: course.licenseType,
          enrollments: allData.enrollments.filter(e => e.courseId === course.id).length
        })),
        enrollments: allData.enrollments.map(enrollment => {
          const student = allData.users.find(u => u.id === enrollment.studentId);
          const instructor = allData.users.find(u => u.id === enrollment.instructorId);
          const course = allData.courses.find(c => c.id === enrollment.courseId);
          return {
            id: enrollment.id,
            studentName: student ? `${student.firstName || ''} ${student.lastName || ''}`.trim() : 'Unknown',
            studentEmail: student?.email || 'Unknown',
            instructorName: instructor ? `${instructor.firstName || ''} ${instructor.lastName || ''}`.trim() : 'Unknown',
            instructorEmail: instructor?.email || 'Unknown',
            courseName: course?.title || 'Unknown',
            courseType: course?.type || 'Unknown',
            totalHours: enrollment.totalHours,
            hoursTaken: enrollment.hoursTaken,
            hoursRemaining: enrollment.hoursRemaining,
            status: enrollment.status,
            createdAt: enrollment.createdAt
          };
        }),
        schedules: allData.schedules.map(schedule => {
          const student = allData.users.find(u => u.id === schedule.studentId);
          const instructor = allData.users.find(u => u.id === schedule.instructorId);
          return {
            id: schedule.id,
            studentName: student ? `${student.firstName || ''} ${student.lastName || ''}`.trim() : 'Unknown',
            instructorName: instructor ? `${instructor.firstName || ''} ${instructor.lastName || ''}`.trim() : 'Unknown',
            date: schedule.date,
            startTime: schedule.startTime,
            endTime: schedule.endTime,
            duration: schedule.duration,
            status: schedule.status,
            venue: schedule.venue,
            createdAt: schedule.createdAt
          };
        }),
        payments: allData.payments.map(payment => {
          const student = allData.users.find(u => u.id === payment.studentId);
          return {
            id: payment.id,
            studentName: student ? `${student.firstName || ''} ${student.lastName || ''}`.trim() : 'Unknown',
            amount: payment.amount,
            currency: payment.currency,
            status: payment.status,
            paymentMethod: payment.paymentMethod,
            createdAt: payment.createdAt
          };
        })
      };
      
      // Create comprehensive Excel report
      const comprehensiveWorksheet = XLSX.utils.json_to_sheet([comprehensiveData.summary]);
      const comprehensiveWorkbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(comprehensiveWorkbook, comprehensiveWorksheet, "Summary");
      
      // Add detailed sheets
      const studentsWorksheet = XLSX.utils.json_to_sheet(comprehensiveData.students);
      XLSX.utils.book_append_sheet(comprehensiveWorkbook, studentsWorksheet, "Students");
      
      const instructorsWorksheet = XLSX.utils.json_to_sheet(comprehensiveData.instructors);
      XLSX.utils.book_append_sheet(comprehensiveWorkbook, instructorsWorksheet, "Instructors");
      
      const coursesWorksheet = XLSX.utils.json_to_sheet(comprehensiveData.courses);
      XLSX.utils.book_append_sheet(comprehensiveWorkbook, coursesWorksheet, "Courses");
      
      const enrollmentsWorksheet = XLSX.utils.json_to_sheet(comprehensiveData.enrollments);
      XLSX.utils.book_append_sheet(comprehensiveWorkbook, enrollmentsWorksheet, "Enrollments");
      
      const schedulesWorksheet = XLSX.utils.json_to_sheet(comprehensiveData.schedules);
      XLSX.utils.book_append_sheet(comprehensiveWorkbook, schedulesWorksheet, "Schedules");
      
      const paymentsWorksheet = XLSX.utils.json_to_sheet(comprehensiveData.payments);
      XLSX.utils.book_append_sheet(comprehensiveWorkbook, paymentsWorksheet, "Payments");
      
      const comprehensiveBuffer = XLSX.write(comprehensiveWorkbook, { bookType: 'xlsx', type: 'array' });
      excelFolder?.file("Comprehensive_Report.xlsx", comprehensiveBuffer);
      
      setBackupProgress("Generating PDF reports...");
      
      // Generate PDF reports
      const generatePDFReport = async (title: string, data: any[], filename: string) => {
        const reportHTML = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>${title}</title>
            <style>
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body { 
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
                margin: 0; 
                padding: 20px; 
                background: white; 
                color: #333;
              }
              .header { 
                text-align: center; 
                margin-bottom: 30px; 
                padding: 20px; 
                background: linear-gradient(135deg, #1e40af, #3b82f6);
                color: white;
                border-radius: 10px;
              }
              .header h1 { font-size: 28px; margin-bottom: 10px; }
              .header p { font-size: 14px; opacity: 0.9; }
              .summary { 
                margin-bottom: 30px; 
                padding: 20px; 
                background: #f8fafc; 
                border-radius: 8px;
                border-left: 4px solid #3b82f6;
              }
              .summary h2 { color: #1e40af; margin-bottom: 15px; }
              .summary-grid { 
                display: grid; 
                grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); 
                gap: 15px; 
              }
              .summary-item { 
                background: white; 
                padding: 15px; 
                border-radius: 6px; 
                box-shadow: 0 1px 3px rgba(0,0,0,0.1);
              }
              .summary-item h3 { color: #64748b; font-size: 12px; margin-bottom: 5px; }
              .summary-item p { font-size: 24px; font-weight: bold; color: #1e40af; }
              table { 
                width: 100%; 
                border-collapse: collapse; 
                margin-bottom: 30px; 
                background: white;
                box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                border-radius: 8px;
                overflow: hidden;
              }
              th { 
                background: #1e40af; 
                color: white; 
                padding: 12px; 
                text-align: left; 
                font-weight: 600;
              }
              td { 
                padding: 12px; 
                border-bottom: 1px solid #e2e8f0; 
              }
              tr:nth-child(even) { background: #f8fafc; }
              tr:hover { background: #f1f5f9; }
              .footer { 
                margin-top: 40px; 
                text-align: center; 
                padding: 20px; 
                color: #64748b; 
                font-size: 12px;
                border-top: 1px solid #e2e8f0;
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>${title}</h1>
              <p>Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
            </div>
            
            <div class="summary">
              <h2>System Summary</h2>
              <div class="summary-grid">
                <div class="summary-item">
                  <h3>Total Students</h3>
                  <p>${comprehensiveData.summary.totalStudents}</p>
                </div>
                <div class="summary-item">
                  <h3>Total Instructors</h3>
                  <p>${comprehensiveData.summary.totalInstructors}</p>
                </div>
                <div class="summary-item">
                  <h3>Total Courses</h3>
                  <p>${comprehensiveData.summary.totalCourses}</p>
                </div>
                <div class="summary-item">
                  <h3>Total Enrollments</h3>
                  <p>${comprehensiveData.summary.totalEnrollments}</p>
                </div>
                <div class="summary-item">
                  <h3>Total Schedules</h3>
                  <p>${comprehensiveData.summary.totalSchedules}</p>
                </div>
                <div class="summary-item">
                  <h3>Total Lessons</h3>
                  <p>${comprehensiveData.summary.totalLessons}</p>
                </div>
                <div class="summary-item">
                  <h3>Total Payments</h3>
                  <p>${comprehensiveData.summary.totalPayments}</p>
                </div>
              </div>
            </div>
            
            <table>
              <thead>
                <tr>
                  ${Object.keys(data[0] || {}).map(key => `<th>${key.charAt(0).toUpperCase() + key.slice(1)}</th>`).join('')}
                </tr>
              </thead>
              <tbody>
                ${data.map(row => `
                  <tr>
                    ${Object.values(row).map(value => `<td>${value || 'N/A'}</td>`).join('')}
                  </tr>
                `).join('')}
              </tbody>
            </table>
            
            <div class="footer">
              <p>DriveWise School Management System - Complete Backup Report</p>
              <p>This report contains all system data as of ${new Date().toLocaleDateString()}</p>
            </div>
          </body>
          </html>
        `;
        
        // Create HTML file for PDF conversion
        const htmlBlob = new Blob([reportHTML], { type: 'text/html' });
        pdfFolder?.file(filename.replace('.pdf', '.html'), htmlBlob);
      };
      
      // Generate PDF reports for each data type
      await generatePDFReport("Students Report", comprehensiveData.students, "Students_Report.pdf");
      await generatePDFReport("Instructors Report", comprehensiveData.instructors, "Instructors_Report.pdf");
      await generatePDFReport("Courses Report", comprehensiveData.courses, "Courses_Report.pdf");
      await generatePDFReport("Enrollments Report", comprehensiveData.enrollments, "Enrollments_Report.pdf");
      await generatePDFReport("Schedules Report", comprehensiveData.schedules, "Schedules_Report.pdf");
      await generatePDFReport("Payments Report", comprehensiveData.payments, "Payments_Report.pdf");
      
      setBackupProgress("Creating ZIP file...");
      
      // Generate the ZIP file
      const zipBlob = await zip.generateAsync({ type: "blob" });
      
      // Download the ZIP file
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `drivewise_backup_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      // Update last backup date
      setLastBackupDate(new Date().toLocaleString());
      setBackupProgress("Backup completed successfully!");
      
      toast({
        title: "Backup Generated Successfully",
        description: "All system data has been exported in PDF and Excel formats.",
      });
      
    } catch (error) {
      console.error("Error generating backup:", error);
      setBackupProgress("Backup failed. Please try again.");
      toast({
        title: "Backup Failed",
        description: "There was an error generating the backup. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingBackup(false);
    }
  };

  return (
    <DashboardLayout role="admin">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">System Backup</h1>
          <p className="text-gray-600 mt-2">
            Generate comprehensive backups of all system data in PDF and Excel formats
          </p>
        </div>

        {/* Backup Status */}
        <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5 text-blue-600" />
                Backup Status
              </CardTitle>
              <CardDescription>
                Current backup information and status
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Last Backup:</span>
                <span className="text-sm text-gray-600">
                  {lastBackupDate || "No backup generated yet"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Status:</span>
                <Badge variant={isGeneratingBackup ? "secondary" : "default"}>
                  {isGeneratingBackup ? "Generating..." : "Ready"}
                </Badge>
              </div>
              {backupProgress && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-blue-600" />
                    <span className="text-sm text-blue-800">{backupProgress}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Archive className="h-5 w-5 text-green-600" />
                Backup Contents
              </CardTitle>
              <CardDescription>
                What will be included in your backup
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="h-4 w-4 text-green-600" />
                <span className="text-sm">Excel files for each collection</span>
              </div>
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-blue-600" />
                <span className="text-sm">PDF reports for each data type</span>
              </div>
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="h-4 w-4 text-purple-600" />
                <span className="text-sm">Comprehensive Excel report</span>
              </div>
              <div className="flex items-center gap-2">
                <Archive className="h-4 w-4 text-orange-600" />
                <span className="text-sm">All data in organized ZIP file</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Data Collections */}
        <Card>
          <CardHeader>
            <CardTitle>Data Collections</CardTitle>
            <CardDescription>
              The following data will be included in your backup
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
                <div>
                  <div className="font-medium">Users</div>
                  <div className="text-sm text-gray-600">Students & Instructors</div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <BookOpen className="h-5 w-5 text-green-600" />
                <div>
                  <div className="font-medium">Courses</div>
                  <div className="text-sm text-gray-600">All course information</div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Calendar className="h-5 w-5 text-purple-600" />
                <div>
                  <div className="font-medium">Schedules</div>
                  <div className="text-sm text-gray-600">Lesson schedules</div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <FileText className="h-5 w-5 text-orange-600" />
                <div>
                  <div className="font-medium">Enrollments</div>
                  <div className="text-sm text-gray-600">Student enrollments</div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <HistoryIcon className="h-5 w-5 text-red-600" />
                <div>
                  <div className="font-medium">Lessons</div>
                  <div className="text-sm text-gray-600">Completed lessons</div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <CreditCard className="h-5 w-5 text-indigo-600" />
                <div>
                  <div className="font-medium">Payments</div>
                  <div className="text-sm text-gray-600">Payment records</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Generate Backup Button */}
        <Card>
          <CardContent className="p-6">
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center gap-2 text-lg font-medium">
                <Database className="h-6 w-6 text-blue-600" />
                Generate System Backup
              </div>
              <p className="text-gray-600 max-w-md mx-auto">
                This will create a comprehensive backup of all system data in both PDF and Excel formats, 
                organized in a downloadable ZIP file.
              </p>
              <Button 
                onClick={generateSystemBackup}
                disabled={isGeneratingBackup}
                size="lg"
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isGeneratingBackup ? (
                  <>
                    <Clock className="mr-2 h-5 w-5 animate-spin" />
                    Generating Backup...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-5 w-5" />
                    Generate Backup
                  </>
                )}
              </Button>
              {isGeneratingBackup && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-2 text-blue-800">
                    <AlertCircle className="h-4 w-4" />
                    <span className="text-sm font-medium">Please wait while the backup is being generated...</span>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default SystemBackup; 